/**
 * DoneView — the close-out.
 *
 * Story 26739. Visual contract: artboard (6) in
 * `tasks/design/step-views.html`.
 *
 * One sentence and a hop to the record. The proof already lived on the
 * Reply-ready pane; repeating it here would make the finish feel like another
 * thing to read.
 *
 * DEVIATION FROM THE TASK TABLE, RECORDED. P1.5 lists five views and the
 * artboards draw six. `stepViewFor` returns `"done"` for every terminal
 * status, so without this the flag-on pane would fall back to the ordinary
 * terminal body and the Done artboard would have no implementation at all —
 * a gap, not a simplification. It is the smallest of the six.
 *
 * No Office.js, no Remix server import (design Constraint 20).
 */
import type { ReactNode } from "react";
import { StepViewFrame, identityLine } from "./StepViewFrame";
import type { StepOutcome } from "./stepViewModel";
import { acknowledgedSummaryLine, type AcknowledgedSummary } from "../warningAcknowledgement";

export const OPEN_RUN_RECORD_LABEL = "Open the run record ›";

export interface DoneViewProps {
  clientName?: string | null;
  policyNumber?: string | null;
  /** "Reply sent 11:47 · endorsement recorded in Epic for the Servicer." */
  sentence?: ReactNode;
  /**
   * The run finished WITHOUT doing the work (already completed on a previous
   * run, rejected, timed out). Only the eyebrow changes — "Done" is true of a
   * terminal run either way, but it reads as "done, and the work landed",
   * which is what let a duplicate-detected run pass for a clean success.
   *
   * The pill is never the sole carrier of the meaning — the sentence and the
   * drawer's "Ended early" warning banner carry it. Same vocabulary as that
   * banner on purpose, so the pane and the banner agree. (The warning tone
   * itself is driven by `outcome`.)
   */
  endedEarly?: boolean;
  /**
   * Whether the run can be CLAIMED to have succeeded (Ruling R35). Anything but
   * `succeeded` renders as a warning: amber pill + amber sentence box, never
   * the calm "Done". REQUIRED: there is no calm default, so a caller cannot
   * forget it and silently render a success look.
   */
  outcome: StepOutcome;
  /**
   * Warning acknowledgement (2026-10-06): every warning line has been
   * acknowledged. Adds ONE calm line under the sentence. It never changes the
   * eyebrow, the tone or the sentence — those still describe the run.
   */
  acknowledged?: AcknowledgedSummary | null;
  /**
   * The run's warning lines with their Acknowledge links (the shared
   * WarningAcknowledgementList). When present, the Done view OWNS the warnings:
   * the drawer stops rendering its own copy of the list, and the one-line
   * `acknowledged` summary is not shown (each line carries its own
   * "Acknowledged by …"). Callers drop `sentence` when it only repeats a line.
   */
  warningLines?: ReactNode;
  /** Omit for no hop — the run record page may not exist in this host yet. */
  onOpenRunRecord?: () => void;
  children?: ReactNode;
}

export function DoneView({
  clientName,
  policyNumber,
  sentence,
  endedEarly,
  outcome,
  acknowledged,
  warningLines,
  onOpenRunRecord,
  children,
}: DoneViewProps) {
  const warn = outcome !== "succeeded";
  const eyebrow =
    outcome === "succeeded_with_warnings"
      ? "Finished with warnings"
      : endedEarly
        ? "Ended early"
        : warn
          ? "Not confirmed"
          : "Done";
  return (
    <StepViewFrame
      view="done"
      eyebrow={eyebrow}
      tone={warn ? "warning" : "quiet"}
      dataOutcome={outcome}
      headline={identityLine(clientName, policyNumber)}
    >
      {sentence ? (
        <p
          className={
            warn
              ? "rounded-md border border-primary-warning-200 bg-primary-warning-50 px-2.5 py-2 text-[12.5px] font-medium leading-relaxed text-primary-warning-700"
              : "text-[12.5px] leading-relaxed text-primary-gray-light-900"
          }
          data-testid="done-sentence"
        >
          {sentence}
        </p>
      ) : null}

      {warningLines ? <div data-testid="done-warning-lines">{warningLines}</div> : null}

      {acknowledged && !warningLines ? (
        <p
          className="text-[11.5px] leading-relaxed text-primary-gray-light-600"
          data-testid="done-acknowledged"
        >
          {acknowledgedSummaryLine(acknowledged)}
        </p>
      ) : null}

      {children}

      {onOpenRunRecord ? (
        <button
          type="button"
          onClick={onOpenRunRecord}
          className="w-fit border-b-2 border-sunrise text-[11.5px] font-semibold text-primary-gray-light-900"
          data-testid="done-run-record"
        >
          {OPEN_RUN_RECORD_LABEL}
        </button>
      ) : null}
    </StepViewFrame>
  );
}

export default DoneView;
